import type { ProductionWorkspaceReport } from "../../../shared/production/workspace-report";
import type { DailyPlanComparisonRow } from "./production-daily-report-comparison";
import type { summarizeDailyPlanComparison } from "./production-daily-report-comparison";
import type { DailyReportFilter, DailyReportChartView } from "./public-daily-report-types";
export const dateText = (value: string) => new Intl.DateTimeFormat("tr-TR", { dateStyle: "long", timeZone: "Europe/Istanbul" }).format(new Date(`${value}T12:00:00Z`));
export const timeText = (value: string) => {
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/u.test(value) ? value : `${value.replace(" ", "T")}+03:00`;
  return Number.isNaN(Date.parse(zoned)) ? "Kayıt zamanı belirtilmemiş" : new Intl.DateTimeFormat("tr-TR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul" }).format(new Date(zoned));
};
export const numberText = (value: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(value);
export const rowId = (row: DailyPlanComparisonRow) => `product-${encodeURIComponent(row.key)}`;

export function publicDailyReportModel(report: ProductionWorkspaceReport, rows: DailyPlanComparisonRow[], summaries: ReturnType<typeof summarizeDailyPlanComparison>, selectedUnit: string | null, filter: DailyReportFilter, chartView: DailyReportChartView, search: string) {
  const activeUnit = selectedUnit ?? (summaries.find((item) => item.unit === "koli")?.unit ?? summaries[0]?.unit ?? null);
  const summary = summaries.find((item) => item.unit === activeUnit);
  const scopedRows = rows.filter((row) => row.unit === activeUnit);
  const systemPlan = report.dailyPlanBasis === "SYSTEM_DAILY";
  const planVerified = systemPlan || report.dailyPlanBasis === "EXCEL_DAILY";
  const comparisonReady = planVerified && !report.truncated;
  const showTargets = Boolean(report.plan) && planVerified;
  const reviewRows = rows.filter((row) => row.needsUnitReview);
  const plans = scopedRows.filter((row) => row.hasPlan);
  const outside = scopedRows.filter((row) => row.actual > 0 && !row.productInDailyPlan);
  const unitMismatch = scopedRows.filter((row) => row.actual > 0 && row.productInDailyPlan && !row.hasPlan);
  const chartRows = comparisonReady ? (chartView === "shortage"
    ? plans.filter((row) => (row.remaining ?? 0) > 0)
    : [...plans].filter((row) => !row.needsUnitReview).sort((left, right) => right.planned - left.planned)).slice(0, 6) : [];
  const chartMaximum = Math.max(1, ...chartRows.flatMap((row) => [row.planned, row.actual]));
  const shiftTotals = ["VR1", "VR2", "VR3", null].map((shift) => ({
    shift, label: shift ? `${shift.slice(-1)}. vardiya` : "Vardiya belirtilmemiş",
    quantity: scopedRows.reduce((sum, row) => sum + row.movements.filter((movement) => movement.documentShiftCode === shift).reduce((total, movement) => total + movement.quantity, 0), 0),
  })).filter((item) => item.shift !== null || item.quantity > 0);
  const maximumShift = Math.max(1, ...shiftTotals.map((item) => item.quantity));
  const query = search.trim().toLocaleLowerCase("tr-TR");
  const visiblePlans = plans.filter((row) => {
    if (query && !`${row.productName} ${row.productCode} ${[...row.planWorkOrders, ...row.otherWorkOrders].join(" ")}`.toLocaleLowerCase("tr-TR").includes(query)) return false;
    if (!comparisonReady || filter === "all") return true;
    if (row.needsUnitReview) return false;
    if (filter === "remaining") return (row.remaining ?? 0) > 0;
    if (filter === "none") return row.actual === 0;
    return row.remaining === 0;
  });
  return { activeUnit, summary, scopedRows, systemPlan, planVerified, comparisonReady, showTargets, reviewRows, plans, outside, unitMismatch, chartRows, chartMaximum, shiftTotals, maximumShift, visiblePlans };
}

export function isDailyReportFilter(value: string): value is DailyReportFilter {
  return value === "all" || value === "remaining" || value === "none" || value === "complete";
}
